import React, { useRef, useEffect, useState, Suspense, useMemo } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Grid } from '@react-three/drei';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import * as THREE from 'three';
import { X, Eye, EyeOff, Grid3X3, Box, Loader, AlertTriangle, RefreshCw, Flame } from 'lucide-react';

// Difficulty → color mapping
function difficultyToColor(difficulty) {
  if (difficulty < 0.33) return new THREE.Color(0x22c55e); // green
  if (difficulty < 0.66) return new THREE.Color(0xf97316); // orange (hot spot)
  return new THREE.Color(0xef4444); // red
}

// Renders contact point spheres as an InstancedMesh for performance
function HeatmapPoints({ contactPoints, centerOffset }) {
  const meshRef = useRef();
  const count = contactPoints?.length || 0;
  const sphereGeo = useMemo(() => new THREE.SphereGeometry(1.2, 8, 8), []);
  const mat = useMemo(() => new THREE.MeshStandardMaterial({
    transparent: true, opacity: 0.85, roughness: 0.3, metalness: 0.1,
  }), []);

  useEffect(() => {
    if (!meshRef.current || count === 0) return;
    const dummy = new THREE.Object3D();
    for (let i = 0; i < count; i++) {
      const pt = contactPoints[i];
      // Apply Z-up → Y-up rotation: (x, y, z) → (x, z, -y) then center
      dummy.position.set(
        pt.position[0] + centerOffset[0],
        pt.position[2] + centerOffset[1],
        -pt.position[1] + centerOffset[2],
      );
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
      meshRef.current.setColorAt(i, difficultyToColor(pt.difficulty));
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
    if (meshRef.current.instanceColor) meshRef.current.instanceColor.needsUpdate = true;
  }, [contactPoints, centerOffset, count]);

  if (count === 0) return null;
  return <instancedMesh ref={meshRef} args={[sphereGeo, mat, count]} />;
}

// Component that loads and displays aligned meshes from pre-loaded geometries
function AlignedMeshesFromGeometry({ 
  modelGeometry,
  supportGeometry,
  showModel, 
  showSupports, 
  modelColor, 
  supportColor, 
  modelOpacity, 
  supportOpacity, 
  showWireframe,
  showHeatmap,
  contactPoints,
}) {
  const groupRef = useRef();
  const { camera } = useThree();
  
  // Calculate combined bounding box and center offset
  const { centerOffset, cameraDistance } = useMemo(() => {
    if (!modelGeometry) return { centerOffset: [0, 0, 0], cameraDistance: 200 };
    
    // Clone and rotate geometries to convert from Z-up to Y-up
    const rotatedModelGeometry = modelGeometry.clone();
    rotatedModelGeometry.rotateX(-Math.PI / 2);
    rotatedModelGeometry.computeBoundingBox();
    const modelBox = rotatedModelGeometry.boundingBox.clone();
    
    let combinedBox = modelBox.clone();
    
    if (supportGeometry) {
      const rotatedSupportGeometry = supportGeometry.clone();
      rotatedSupportGeometry.rotateX(-Math.PI / 2);
      rotatedSupportGeometry.computeBoundingBox();
      combinedBox.union(rotatedSupportGeometry.boundingBox);
    }
    
    const center = new THREE.Vector3();
    combinedBox.getCenter(center);
    
    const size = new THREE.Vector3();
    combinedBox.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    
    return {
      centerOffset: [-center.x, -center.y, -center.z],
      cameraDistance: maxDim * 2
    };
  }, [modelGeometry, supportGeometry]);
  
  // Set initial camera position
  useEffect(() => {
    if (cameraDistance) {
      camera.position.set(cameraDistance * 0.7, cameraDistance * 0.7, cameraDistance * 0.7);
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();
    }
  }, [camera, cameraDistance]);
  
  // Clone and prepare geometries
  const processedModelGeometry = useMemo(() => {
    if (!modelGeometry) return null;
    const clone = modelGeometry.clone();
    clone.rotateX(-Math.PI / 2);
    clone.computeVertexNormals();
    return clone;
  }, [modelGeometry]);
  
  const processedSupportGeometry = useMemo(() => {
    if (!supportGeometry) return null;
    const clone = supportGeometry.clone();
    clone.rotateX(-Math.PI / 2);
    clone.computeVertexNormals();
    return clone;
  }, [supportGeometry]);
  
  return (
    <group ref={groupRef} position={centerOffset}>
      {/* Model mesh */}
      {showModel && processedModelGeometry && (
        <mesh geometry={processedModelGeometry}>
          <meshStandardMaterial 
            color={modelColor} 
            opacity={modelOpacity}
            transparent={modelOpacity < 1}
            wireframe={showWireframe}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      
      {/* Support mesh */}
      {showSupports && processedSupportGeometry && (
        <mesh geometry={processedSupportGeometry}>
          <meshStandardMaterial 
            color={supportColor} 
            opacity={supportOpacity}
            transparent={supportOpacity < 1}
            wireframe={showWireframe}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      
      {/* Easy-Peel Heatmap */}
      {showHeatmap && contactPoints && contactPoints.length > 0 && (
        <HeatmapPoints contactPoints={contactPoints} centerOffset={[0, 0, 0]} />
      )}
      
      <OrbitControls makeDefault enableDamping={false} />
    </group>
  );
}

// Loading placeholder
function LoadingFallback() {
  return (
    <mesh>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="#666" wireframe />
    </mesh>
  );
}

// Async function to load STL from URL with progress tracking and timeout
async function loadSTLFromUrl(url, onProgress, timeoutMs = 120000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`Failed to load STL: ${response.status} ${response.statusText}`);
    }
    
    // Get content length for progress tracking
    const contentLength = response.headers.get('Content-Length');
    const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;
    
    // Read response body with progress tracking
    const reader = response.body.getReader();
    const chunks = [];
    let receivedBytes = 0;
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      chunks.push(value);
      receivedBytes += value.length;
      
      // Report progress
      if (onProgress && totalBytes > 0) {
        onProgress({
          loaded: receivedBytes,
          total: totalBytes,
          percent: Math.round((receivedBytes / totalBytes) * 100)
        });
      }
    }
    
    // Combine chunks into single ArrayBuffer
    const arrayBuffer = new Uint8Array(receivedBytes);
    let offset = 0;
    for (const chunk of chunks) {
      arrayBuffer.set(chunk, offset);
      offset += chunk.length;
    }
    
    const loader = new STLLoader();
    return loader.parse(arrayBuffer.buffer);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Loading timed out. The file may be too large. Try downloading instead of previewing.');
    }
    throw error;
  }
}

// Main STL Viewer Component
export default function STLViewer({ 
  modelUrl, 
  supportUrl, 
  showModel = true,
  showSupports = true,
  modelColor = '#4a90d9',
  supportColor = '#f59e0b',
  modelOpacity = 0.7,
  supportOpacity = 1,
  showWireframe = false,
  showGrid = true,
  backgroundColor = '#1a1a2e',
  showHeatmap = false,
  contactPoints = [],
}) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ model: 0, support: 0 });
  const [loadingStatus, setLoadingStatus] = useState('Initializing...');
  const [modelGeometry, setModelGeometry] = useState(null);
  const [supportGeometry, setSupportGeometry] = useState(null);
  const [canRetry, setCanRetry] = useState(false);
  
  // Load STL files with progress
  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    setCanRetry(false);
    setLoadingProgress({ model: 0, support: 0 });
    
    try {
      // Load model
      if (modelUrl) {
        setLoadingStatus('Loading model...');
        const modelGeo = await loadSTLFromUrl(modelUrl, (progress) => {
          setLoadingProgress(prev => ({ ...prev, model: progress.percent }));
          setLoadingStatus(`Loading model... ${progress.percent}% (${(progress.loaded / 1024 / 1024).toFixed(1)} MB)`);
        });
        setModelGeometry(modelGeo);
      }
      
      // Load supports
      if (supportUrl) {
        setLoadingStatus('Loading supports...');
        try {
          const supportGeo = await loadSTLFromUrl(supportUrl, (progress) => {
            setLoadingProgress(prev => ({ ...prev, support: progress.percent }));
            setLoadingStatus(`Loading supports... ${progress.percent}% (${(progress.loaded / 1024 / 1024).toFixed(1)} MB)`);
          });
          setSupportGeometry(supportGeo);
        } catch (e) {
          console.warn('Failed to load support mesh:', e);
          // Don't fail completely if supports don't load
        }
      }
      
      setLoading(false);
      setLoadingStatus('Complete');
    } catch (e) {
      console.error('Failed to load STL:', e);
      setError(e.message);
      setLoading(false);
      setCanRetry(true);
    }
  };
  
  useEffect(() => {
    loadFiles();
  }, [modelUrl, supportUrl]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-bg-dark rounded-lg border border-red-500/30">
        <div className="text-center p-6 max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-red-400 font-medium mb-2">Failed to load 3D preview</p>
          <p className="text-sm text-text-secondary mb-4">{error}</p>
          {canRetry && (
            <button
              onClick={loadFiles}
              className="px-4 py-2 bg-accent hover:bg-accent/80 text-white rounded-lg flex items-center gap-2 mx-auto transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          )}
          <p className="text-xs text-text-muted mt-4">
            Tip: Large support files (&gt;20MB) may take longer to load. 
            If preview fails repeatedly, try downloading the files instead.
          </p>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-bg-dark rounded-lg">
        <div className="text-center p-6">
          <Loader className="w-10 h-10 text-accent animate-spin mx-auto mb-4" />
          <p className="text-white font-medium mb-2">{loadingStatus}</p>
          
          {/* Progress bars */}
          <div className="w-64 space-y-3 mt-4">
            {modelUrl && (
              <div>
                <div className="flex justify-between text-xs text-text-secondary mb-1">
                  <span>Model</span>
                  <span>{loadingProgress.model}%</span>
                </div>
                <div className="h-2 bg-bg-light rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${loadingProgress.model}%` }}
                  />
                </div>
              </div>
            )}
            {supportUrl && (
              <div>
                <div className="flex justify-between text-xs text-text-secondary mb-1">
                  <span>Supports</span>
                  <span>{loadingProgress.support}%</span>
                </div>
                <div className="h-2 bg-bg-light rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 transition-all duration-300"
                    style={{ width: `${loadingProgress.support}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          
          <p className="text-xs text-text-muted mt-4">
            Large files may take a moment to load...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full rounded-lg overflow-hidden" style={{ backgroundColor }}>
      <Canvas
        camera={{ position: [100, 100, 100], fov: 50 }}
        gl={{ antialias: true, preserveDrawingBuffer: true }}
      >
        <ambientLight intensity={0.5} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <directionalLight position={[-10, -10, -5]} intensity={0.5} />
        
        <Suspense fallback={<LoadingFallback />}>
          {modelGeometry && (
            <AlignedMeshesFromGeometry
              modelGeometry={modelGeometry}
              supportGeometry={supportGeometry}
              showModel={showModel}
              showSupports={showSupports}
              modelColor={modelColor}
              supportColor={supportColor}
              modelOpacity={modelOpacity}
              supportOpacity={supportOpacity}
              showWireframe={showWireframe}
              showHeatmap={showHeatmap}
              contactPoints={contactPoints}
            />
          )}
        </Suspense>
        
        {/* Grid floor */}
        {showGrid && (
          <Grid 
            position={[0, -50, 0]} 
            args={[200, 200]} 
            cellSize={10}
            cellThickness={0.5}
            cellColor="#444"
            sectionSize={50}
            sectionThickness={1}
            sectionColor="#666"
            fadeDistance={400}
            fadeStrength={1}
            infiniteGrid={false}
          />
        )}
      </Canvas>
    </div>
  );
}

// Export the viewer with controls as a modal
export function STLViewerWithControls({ modelUrl, supportUrl, onClose, contactPoints = [] }) {
  const [showModel, setShowModel] = useState(true);
  const [showSupports, setShowSupports] = useState(true);
  const [showWireframe, setShowWireframe] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [modelOpacity, setModelOpacity] = useState(0.7);
  const [supportOpacity, setSupportOpacity] = useState(1);
  const [modelColor, setModelColor] = useState('#4a90d9');
  const [supportColor, setSupportColor] = useState('#f59e0b');

  const heatmapStats = useMemo(() => {
    if (!contactPoints || contactPoints.length === 0) return null;
    const easy = contactPoints.filter(p => p.label === 'easy').length;
    const moderate = contactPoints.filter(p => p.label === 'moderate').length;
    const hard = contactPoints.filter(p => p.label === 'hard').length;
    return { easy, moderate, hard, total: contactPoints.length };
  }, [contactPoints]);

  return (
    <div data-testid="stl-viewer-modal" className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-bg-dark rounded-xl w-full max-w-6xl h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-text-muted/20">
          <h2 className="text-xl font-bold text-white">3D Support Preview</h2>
          <button
            onClick={onClose}
            data-testid="close-viewer-btn"
            className="p-2 hover:bg-bg-light rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-text-secondary" />
          </button>
        </div>
        
        {/* Main content */}
        <div className="flex-1 flex">
          {/* 3D Viewer */}
          <div className="flex-1 p-4">
            <STLViewer
              modelUrl={modelUrl}
              supportUrl={supportUrl}
              showModel={showModel}
              showSupports={showSupports}
              modelColor={modelColor}
              supportColor={supportColor}
              modelOpacity={modelOpacity}
              supportOpacity={supportOpacity}
              showWireframe={showWireframe}
              showGrid={showGrid}
              showHeatmap={showHeatmap}
              contactPoints={contactPoints}
            />
          </div>
          
          {/* Controls Panel */}
          <div className="w-64 p-4 border-l border-text-muted/20 space-y-4 overflow-y-auto">
            <h3 className="text-white font-semibold">View Controls</h3>
            
            {/* Toggle Model */}
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-2">
                <Box className="w-4 h-4 text-blue-400" />
                <span className="text-text-secondary">Show Model</span>
              </div>
              <input
                type="checkbox"
                checked={showModel}
                onChange={(e) => setShowModel(e.target.checked)}
                className="w-4 h-4 accent-accent"
              />
            </label>
            
            {/* Toggle Supports */}
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-orange-400" />
                <span className="text-text-secondary">Show Supports</span>
              </div>
              <input
                type="checkbox"
                checked={showSupports}
                onChange={(e) => setShowSupports(e.target.checked)}
                className="w-4 h-4 accent-accent"
              />
            </label>

            {/* Toggle Heatmap */}
            {contactPoints.length > 0 && (
              <label data-testid="heatmap-toggle" className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-2">
                  <Flame className="w-4 h-4 text-red-400" />
                  <span className="text-text-secondary">Peel Heatmap</span>
                </div>
                <input
                  type="checkbox"
                  checked={showHeatmap}
                  onChange={(e) => setShowHeatmap(e.target.checked)}
                  className="w-4 h-4 accent-accent"
                />
              </label>
            )}
            
            {/* Toggle Wireframe */}
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-2">
                <Grid3X3 className="w-4 h-4 text-text-muted" />
                <span className="text-text-secondary">Wireframe</span>
              </div>
              <input
                type="checkbox"
                checked={showWireframe}
                onChange={(e) => setShowWireframe(e.target.checked)}
                className="w-4 h-4 accent-accent"
              />
            </label>
            
            {/* Toggle Grid */}
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-2">
                <Grid3X3 className="w-4 h-4 text-text-muted" />
                <span className="text-text-secondary">Show Grid</span>
              </div>
              <input
                type="checkbox"
                checked={showGrid}
                onChange={(e) => setShowGrid(e.target.checked)}
                className="w-4 h-4 accent-accent"
              />
            </label>
            
            {/* Model Opacity */}
            <div>
              <label className="block text-text-secondary text-sm mb-1">Model Opacity</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={modelOpacity}
                onChange={(e) => setModelOpacity(parseFloat(e.target.value))}
                className="w-full accent-blue-400"
              />
            </div>
            
            {/* Support Opacity */}
            <div>
              <label className="block text-text-secondary text-sm mb-1">Support Opacity</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={supportOpacity}
                onChange={(e) => setSupportOpacity(parseFloat(e.target.value))}
                className="w-full accent-orange-400"
              />
            </div>
            
            {/* Color Pickers */}
            <div className="pt-4 border-t border-text-muted/20">
              <h4 className="text-white font-medium mb-3">Colors</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Model</span>
                  <input
                    type="color"
                    value={modelColor}
                    onChange={(e) => setModelColor(e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Supports</span>
                  <input
                    type="color"
                    value={supportColor}
                    onChange={(e) => setSupportColor(e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Heatmap Legend */}
            {contactPoints.length > 0 && showHeatmap && (
              <div data-testid="heatmap-legend" className="pt-4 border-t border-text-muted/20">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Flame className="w-4 h-4 text-red-400" />
                  Peel Difficulty
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-text-secondary text-sm">Easy</span>
                    {heatmapStats && <span className="text-text-muted text-xs ml-auto">{heatmapStats.easy}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500" />
                    <span className="text-text-secondary text-sm">Moderate</span>
                    {heatmapStats && <span className="text-text-muted text-xs ml-auto">{heatmapStats.moderate}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-text-secondary text-sm">Hard</span>
                    {heatmapStats && <span className="text-text-muted text-xs ml-auto">{heatmapStats.hard}</span>}
                  </div>
                </div>
                {heatmapStats && (
                  <p className="text-xs text-text-muted mt-2">
                    {heatmapStats.total} contact points total
                  </p>
                )}
              </div>
            )}
            
            {/* Instructions */}
            <div className="pt-4 border-t border-text-muted/20 text-xs text-text-secondary">
              <p className="mb-1">Left click + drag to rotate</p>
              <p className="mb-1">Right click + drag to pan</p>
              <p>Scroll to zoom</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
